import React from 'react';
import './DecisionCockpit.css';

const DecisionCockpit = ({ lastRun }) => {
  // Dados de exemplo para quando não há última execução
  const exampleRun = {
    eventId: 'event-001',
    eventTitle: 'China invade Taiwan até 2027?',
    thesisId: 'thesis-001',
    thesisTitle: 'Escalada militar + precedentes históricos + deterioração diplomática',
    probability: 0.63,
    confidence: 0.72,
    expectedValue: 1.25,
    kellyFraction: 0.043,
    decision: 'EXECUTE',
    rationale: 'Convergência de evidências militares, diplomáticas e históricas suportam alta probabilidade de conflito nos próximos 3 anos.',
    marketId: 'market-001',
    platform: 'Polymarket',
    marketPrice: 0.45,
    recommendedAllocation: 0.043,
    confidenceLevel: 'HIGH',
    runId: 'run-example',
    executedAt: new Date().toISOString(),
  };

  const run = lastRun || exampleRun;

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDecisionStatusClass = (decision) => {
    switch (decision) {
      case 'EXECUTE': return 'status-execute';
      case 'AVOID': return 'status-avoid';
      case 'WATCH': return 'status-watch';
      default: return '';
    }
  };

  const evidenceItems = [
    'Exercícios militares na região aumentaram 300% nos últimos 12 meses',
    'Retórica oficial chinesa tornou-se mais assertiva',
    'Sanções econômicas cruzadas em escalada',
    'Precedentes históricos de conflitos similares',
    'Alinhamento estratégico com Rússia em questões de soberania',
  ];

  return (
    <div className="decision-cockpit">
      <div className="cockpit-header">
        <h2>🚀 Cockpit de Decisão</h2>
        <div className="execution-info">
          <span className="run-id">Execução: {run.runId?.substring(0, 8)}...</span>
          <span className="execution-time">Executado em: {formatDate(run.executedAt)}</span>
        </div>
      </div>

      <div className="grid grid-3">
        {/* Bloco 1 - Evento */}
        <div className="card">
          <div className="card-header">
            <span className="icon">📅</span>
            <h3>Evento</h3>
          </div>
          <div className="card-content">
            <h4 className="card-title">{run.eventTitle}</h4>
            <p className="event-id">ID: {run.eventId}</p>
            <div className="event-meta">
              <span className="meta-item">Categoria: Geopolítica</span>
              <span className="meta-item">Prazo: 2027</span>
            </div>
          </div>
        </div>

        {/* Bloco 2 - Tese */}
        <div className="card">
          <div className="card-header">
            <span className="icon">🎯</span>
            <h3>Tese</h3>
          </div>
          <div className="card-content">
            <h4 className="card-title">{run.thesisTitle}</h4>
            <p className="thesis-id">ID: {run.thesisId}</p>
            <div className="thesis-rationale">
              <p>{run.rationale}</p>
            </div>
          </div>
        </div>

        {/* Bloco 3 - Evidências */}
        <div className="card">
          <div className="card-header">
            <span className="icon">🔍</span>
            <h3>Evidências</h3>
          </div>
          <div className="card-content">
            <ul className="evidence-list">
              {evidenceItems.slice(0, 5).map((evidence, index) => (
                <li key={index} className="evidence-item">
                  <span className="evidence-bullet">•</span>
                  {evidence}
                </li>
              ))}
            </ul>
            <div className="evidence-count">
              {evidenceItems.length} evidências analisadas
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-3">
        {/* Bloco 4 - Confiança */}
        <div className="card">
          <div className="card-header">
            <span className="icon">📊</span>
            <h3>Confiança</h3>
          </div>
          <div className="card-content">
            <div className="confidence-metrics">
              <div className="metric">
                <span className="metric-label">Probabilidade</span>
                <span className="metric-value probability">
                  {formatPercentage(run.probability)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Confiança</span>
                <span className="metric-value confidence">
                  {formatPercentage(run.confidence)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Valor Esperado</span>
                <span className="metric-value ev">
                  {run.expectedValue?.toFixed(2)}x
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Fração Kelly</span>
                <span className="metric-value kelly">
                  {formatPercentage(run.kellyFraction)}
                </span>
              </div>
              <div className="metric">
                <span className="metric-label">Nível Confiança</span>
                <span className={`metric-value confidence-level ${run.confidenceLevel?.toLowerCase()}`}>
                  {run.confidenceLevel}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bloco 5 - Decisão */}
        <div className="card">
          <div className="card-header">
            <span className="icon">⚡</span>
            <h3>Decisão</h3>
          </div>
          <div className="card-content">
            <div className="decision-main">
              <div className={`decision-status ${getDecisionStatusClass(run.decision)}`}>
                {run.decision}
              </div>
              <div className="decision-details">
                <div className="detail">
                  <span className="detail-label">Posição:</span>
                  <span className="detail-value">YES</span>
                </div>
                <div className="detail">
                  <span className="detail-label">Stake Recomendado:</span>
                  <span className="detail-value stake">
                    {formatPercentage(run.recommendedAllocation)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bloco 6 - Mercado */}
        <div className="card">
          <div className="card-header">
            <span className="icon">💹</span>
            <h3>Mercado</h3>
          </div>
          <div className="card-content">
            <div className="market-info">
              <div className="market-platform">
                <span className="platform-label">Plataforma:</span>
                <span className="platform-name">{run.platform}</span>
              </div>
              <div className="market-price">
                <span className="price-label">Preço de Mercado:</span>
                <span className="price-value">
                  {formatPercentage(run.marketPrice)}
                </span>
              </div>
              <div className="market-id">
                <span className="id-label">ID Mercado:</span>
                <span className="id-value">{run.marketId}</span>
              </div>
            </div>
            <div className="market-arbitrage">
              <div className="arbitrage-metric">
                <span className="metric-label">Discrepância:</span>
                <span className="metric-value discrepancy positive">
                  +{formatPercentage(Math.abs(run.probability - run.marketPrice))}
                </span>
              </div>
              <div className="arbitrage-note">
                Probabilidade do modelo vs. preço de mercado
              </div>
            </div>
          </div>
        </div>
      </div>

      {!lastRun && (
        <div className="cockpit-note">
          <p>💡 Este é um exemplo do cockpit. Execute uma decisão real para ver dados atualizados.</p>
        </div>
      )}
    </div>
  );
};

export default DecisionCockpit;